import React, { useEffect, useMemo } from 'react';
import { HbrDonutChart } from '@harbor/elements-react-standalone';
import { TileDefinition } from '../../../types/dashboard';
import type { TileSettings } from '../../../types/tileBuilderTypes';
import { buildHarborItemTooltip } from '../../../support/harborTooltip';
import { getContextualChartMargins } from './chartLayoutUtils';
import { makeSmartValueFormatter } from './tileUtils';

interface ChartData {
  id: string;
  label: string;
  value: number;
}

interface PieChartTileProps {
  data: ChartData[];
  tileDef: TileDefinition;
  layoutHeight: number;
  layoutWidth: number;
  showLegendOverride?: boolean;
  setError?: (error: string) => void;
  initialized?: boolean;
  displayContext?: 'preview' | 'dashboard' | 'leader';
}

function formatCenterValue(total: number, s: any) {
  const fmt = makeSmartValueFormatter({
    valueFormat:
      s?.centerValueFormat === 'raw'
        ? 'number'
        : s?.centerValueFormat === 'suffix'
          ? 'number'
          : s?.centerValueFormat,
    currency: s?.currency,
    numberNotation:
      s?.centerValueFormat === 'suffix' ? 'compact' : (s?.numberNotation ?? 'standard'),
    compactThreshold: s?.compactThreshold,
    compactDecimals: s?.compactDecimals,
    prefixCurrencyOnCompact: s?.prefixCurrencyOnCompact,
  });

  if (s?.centerValueFormat === 'raw') return String(total);
  return fmt(total);
}

const PieChartTile: React.FC<PieChartTileProps> = ({
                                                     tileDef,
                                                     data,
                                                     layoutHeight,
                                                     layoutWidth,
                                                     showLegendOverride,
                                                     setError,
                                                     initialized = false,
                                                     displayContext = 'dashboard',
                                                   }) => {
  const s = useMemo(() => (tileDef?.settings ?? {}) as TileSettings & any, [tileDef?.settings]);

  const isValidArray = Array.isArray(data);
  const dataArray = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const w = Math.max(1, layoutWidth || 0);
  const h = Math.max(1, layoutHeight || 0);

  useEffect(() => {
    if (initialized && !isValidArray) {
      console.error('Expected pie data to be an array, but got:', data);
      setError?.('Invalid data format');
    }
  }, [initialized, isValidArray, data, setError]);

  const total = useMemo(() => {
    return dataArray.reduce((acc, cur) => acc + (Number(cur?.value) || 0), 0);
  }, [dataArray]);

  const valueFormatter = useMemo(
    () =>
      makeSmartValueFormatter({
        valueFormat: s?.valueFormat,
        currency: s?.currency,
        numberNotation: s?.numberNotation,
        compactThreshold: s?.compactThreshold,
        compactDecimals: s?.compactDecimals,
        prefixCurrencyOnCompact: s?.prefixCurrencyOnCompact,
      }),
    [
      s?.valueFormat,
      s?.currency,
      s?.numberNotation,
      s?.compactThreshold,
      s?.compactDecimals,
      s?.prefixCurrencyOnCompact,
    ]
  );

  const showLegend = useMemo(() => {
    if (showLegendOverride !== undefined) return showLegendOverride;
    return layoutHeight > 150 && !!(tileDef?.show_legend || s?.show_legend);
  }, [showLegendOverride, layoutHeight, tileDef?.show_legend, s?.show_legend]);

  const layoutStrategy: 'auto' | 'manual' = s?.layoutStrategy === 'manual' ? 'manual' : 'auto';

  const computedMargin = useMemo(() => {
    const manualMargin = getContextualChartMargins(s, displayContext, showLegend, {
      top: 30,
      right: 10,
      bottom: 55,
      left: showLegend ? 150 : 10,
    });

    if (layoutStrategy === 'manual' && manualMargin) {
      return manualMargin;
    }

    return {
      top: typeof s?.marginTop === 'number' ? s.marginTop : 30,
      right: typeof s?.marginRight === 'number' ? s.marginRight : 10,
      bottom: typeof s?.marginBottom === 'number' ? s.marginBottom : 55,
      left: typeof s?.marginLeft === 'number' ? s.marginLeft : showLegend ? 150 : 10,
    };
  }, [
    s,
    displayContext,
    showLegend,
    layoutStrategy,
  ]);

  const innerRadius = 0;
  const enableArcLinkLabels = !!s?.enableArcLinkLabels;
  const enableArcLabels = !!s?.enableArcLabels;

  const itemTooltip = useMemo(() => {
    return buildHarborItemTooltip(
      s,
      {
        getSeries: (e: any) =>
          e?.id ?? e?.key ?? e?.data?.id ?? e?.data?.key ?? e?.data?.label ?? '',
        getLabel: (e: any) => e?.data?.label ?? e?.label ?? e?.data?.id ?? e?.id ?? '',
        getValue: (e: any) => {
          const raw = e?.data?.value ?? e?.value ?? '';
          const n = Number(raw);
          return Number.isFinite(n) ? valueFormatter(n) : raw;
        },
      },
      { dotMode: 'before' }
    );
  }, [s, valueFormatter]);

  const legendTooltip = useMemo(() => {
    if (s?.enableLegendTooltip === false) return () => false;

    return buildHarborItemTooltip(
      s,
      {
        getSeries: (e: any) => e?.id ?? e?.label ?? '',
        getLabel: (e: any) => e?.label ?? e?.id ?? '',
        getValue: (e: any) => {
          const key = String(e?.id ?? e?.label ?? '');
          if (!key) return '';
          const match = dataArray.find(
            (d: any) => String(d?.id ?? d?.label ?? '') === key
          );
          const v = match ? Number(match.value) : NaN;
          return Number.isFinite(v) ? valueFormatter(v) : '';
        },
      },
      { dotMode: 'before' }
    );
  }, [s, dataArray, valueFormatter]);

  const legends = useMemo(() => {
    if (!showLegend) return [];

    return [
      {
        direction: s?.legendDirection ?? 'column',
        align: 'left',
        translateX: typeof s?.legendTranslateX === 'number' ? s.legendTranslateX : -200,
        translateY: typeof s?.legendTranslateY === 'number' ? s.legendTranslateY : -10,
        itemWidth: typeof s?.legendItemWidth === 'number' ? s.legendItemWidth : 104,
        itemHeight: typeof s?.legendItemHeight === 'number' ? s.legendItemHeight : 20,
        itemsSpacing: typeof s?.legendItemsSpacing === 'number' ? s.legendItemsSpacing : 5,
        symbolSize: typeof s?.legendSymbolSize === 'number' ? s.legendSymbolSize : 18,
      },
    ];
  }, [
    showLegend,
    s?.legendDirection,
    s?.legendTranslateX,
    s?.legendTranslateY,
    s?.legendItemWidth,
    s?.legendItemHeight,
    s?.legendItemsSpacing,
    s?.legendSymbolSize,
  ]);

  const centerContent = useMemo(() => {
    const centerMode = (s?.centerMode ?? 'total') as 'none' | 'total' | 'custom' | 'slot';

    if (centerMode === 'none') return undefined;

    if (centerMode === 'custom') {
      const html = String(s?.centerHtml ?? '').trim();
      if (!html) return undefined;
      return () => html;
    }

    if (centerMode === 'slot') return undefined;

    const value = formatCenterValue(total, s);
    const title = String(s?.centerTitle ?? '').trim();
    const subtitle = String(s?.centerSubtitle ?? '').trim();

    return () => `
      <div style="display:flex;height:100%;flex-direction:column;justify-content:center;align-items:center;gap:2px;text-align:center;">
        ${title ? `<div style="font-size:var(--hbr-font-size-xs);color:var(--base-text-medium-default)">${title}</div>` : ''}
        <div style="font-size:var(--hbr-font-size-2xl);font-weight:var(--hbr-font-weight-bold,700)">${value}</div>
        ${subtitle ? `<div style="font-size:var(--hbr-font-size-xs);color:var(--base-text-medium-default)">${subtitle}</div>` : ''}
      </div>
    `;
  }, [s, total]);

  const popoverOptions = useMemo(() => {
    return {
      ...(s?.popoverOptions ?? {}),
      hoist: true,
    };
  }, [s?.popoverOptions]);

  const config = useMemo(() => {
    return {
      data: dataArray,
      margin: computedMargin,
      innerRadius,
      legends,
      enableArcLinkLabels,
      enableArcLabels,

      isInteractive: s?.isInteractive !== false,
      isFocusable: !!s?.isFocusable,

      itemTooltip,
      legendTooltip,
      popoverOptions,

      ...(s?.useDatumColorField ? { colors: (d: any) => d?.data?.color ?? d?.color } : {}),
      ...(centerContent ? { centerContent } : {}),
    };
  }, [
    dataArray,
    computedMargin,
    innerRadius,
    legends,
    enableArcLinkLabels,
    enableArcLabels,
    s?.isInteractive,
    s?.isFocusable,
    s?.useDatumColorField,
    itemTooltip,
    legendTooltip,
    centerContent,
    popoverOptions,
  ]);

  const onHbrModifyConfig = useMemo(() => {
    return (currentConfig: any) => ({
      ...currentConfig,
      popoverOptions: {
        ...(currentConfig?.popoverOptions ?? {}),
        ...popoverOptions,
        hoist: true,
      },
    });
  }, [popoverOptions]);

  if (!setError && initialized && !isValidArray) {
    return <div>Invalid data format</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: showLegend ? 'flex-start' : 'center',
        alignItems: 'center',
        height: '100%',
        position: 'relative',
      }}
    >
      <HbrDonutChart
        key={`pie-${w}x${h}-${displayContext}-${showLegend ? 'legend' : 'base'}`}
        variant="pie"
        width="100%"
        height="100%"
        config={config}
        modifyHbrConfig={onHbrModifyConfig}
      />
    </div>
  );
};

export default PieChartTile;